import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { api } from "../lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking
  const [ready, setReady] = useState(false);
  const userSetByLogin = useRef(false); // prevent stale /me responses from overwriting fresh login

  useEffect(() => {
    let cancelled = false;
    // Skip the /auth/me probe entirely when there's no token in localStorage.
    // Cuts ~150-300ms off the initial paint for first-time visitors / logged-out
    // users (they don't need to wait for a 401 round-trip before the login UI shows).
    if (!localStorage.getItem("token")) {
      setUser(false);
      setReady(true);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (cancelled || userSetByLogin.current) return;
        setUser(data);
      } catch {
        if (cancelled || userSetByLogin.current) return;
        setUser(false);
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.token) localStorage.setItem("token", data.token);
    userSetByLogin.current = true;
    setUser(data);
    setReady(true);
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    if (data.token) localStorage.setItem("token", data.token);
    userSetByLogin.current = true;
    setUser(data);
    setReady(true);
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
    localStorage.removeItem("token");
    userSetByLogin.current = false;
    setUser(false);
  };

  const refreshUser = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (e) { /* ignore */ }
  };

  return (
    <AuthContext.Provider value={{ user, ready, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
